import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BlueprintsTableView,
  type BlueprintRowVM,
} from './BlueprintsTableView';
import { ACCEPTED_EXTS, validate } from './blueprintValidation';

export type StagedBlueprint = {
  localId: string;
  file: File;
  label: string;
  status: 'staged' | 'uploading' | 'failed';
  errorMessage?: string;
};

interface BlueprintsStagingProps {
  staged: StagedBlueprint[];
  onChange: (next: StagedBlueprint[]) => void;
}

function makeLocalId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `staged-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function BlueprintsStaging({
  staged,
  onChange,
}: BlueprintsStagingProps) {
  const { t } = useTranslation();
  const addInputRef = useRef<HTMLInputElement | null>(null);
  const replaceInputRef = useRef<HTMLInputElement | null>(null);
  const replaceTargetRef = useRef<string | null>(null);

  const uploading = staged.some((s) => s.status === 'uploading');

  const handleAdd = (file: File) => {
    onChange([
      ...staged,
      {
        localId: makeLocalId(),
        file,
        label: file.name,
        status: 'staged',
      },
    ]);
  };

  const handleReplace = (localId: string, file: File) => {
    onChange(
      staged.map((s) =>
        s.localId === localId
          ? {
            ...s,
            file,
            status: 'staged',
            errorMessage: undefined,
          }
          : s,
      ),
    );
  };

  const handleDelete = (localId: string) => {
    onChange(staged.filter((s) => s.localId !== localId));
  };

  const handleEditLabel = (localId: string, label: string) => {
    onChange(
      staged.map((s) => (s.localId === localId ? { ...s, label } : s)),
    );
  };

  const rows: BlueprintRowVM[] = staged.map((s) => ({
    id: s.localId,
    label: s.label,
    fileName: s.file.name,
    status: s.status,
    errorMessage: s.errorMessage,
  }));

  return (
    <>
      <input
        ref={addInputRef}
        type="file"
        accept={ACCEPTED_EXTS}
        hidden
        aria-label={t('sites.stageNewBlueprintAria', 'Stage new blueprint')}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (validate(file)) handleAdd(file);
          e.target.value = '';
        }}
        data-e2e="file-input-add-blueprint"
      />
      <input
        ref={replaceInputRef}
        type="file"
        accept={ACCEPTED_EXTS}
        hidden
        aria-label={t('sites.replaceStagedBlueprintFileAria', 'Replace staged blueprint file')}
        onChange={(e) => {
          const file = e.target.files?.[0];
          const target = replaceTargetRef.current;
          replaceTargetRef.current = null;
          if (target != null && validate(file)) handleReplace(target, file);
          e.target.value = '';
        }}
        data-e2e="file-input-replace-blueprint"
      />
      <BlueprintsTableView
        rows={rows}
        addLabel={t('sites.addBlueprint', 'Add Blueprint')}
        addDisabled={uploading}
        onAddFileClick={() => addInputRef.current?.click()}
        onEditLabel={handleEditLabel}
        onReplaceClick={(id) => {
          replaceTargetRef.current = id;
          replaceInputRef.current?.click();
        }}
        onDelete={handleDelete}
        editingDisabled={uploading}
        replaceDisabled={uploading}
        deleteDisabled={uploading}
        helperText={t('sites.blueprintsStagedHelperText', 'Reference artifacts will be uploaded after the site is saved.')}
      />
    </>
  );
}
